import React, { useMemo, useState, useEffect } from 'react'
import { Box, Grid, Rating, Typography } from '@mui/material'
import TableData from '../atoms/table/TableData'
import StatusBtn from '../atoms/status/StatusBtn'
import ButtonCustom from '../atoms/button/ButtonCustom'
import KebabMenu from '../dataGrid/KebabMenu'
import CommonModal from '../atoms/modal/CommonModal'
import { closeModal, openModal, setModalData } from '../../features/commonSlice'
import { useDispatch, useSelector } from 'react-redux'
import TextArea from '../atoms/textarea/TextArea'
import styles from './index.module.scss'
import { useNavigate, useParams } from 'react-router-dom'
import { formatCommonDate } from '../../util/formatDateAndTime'
import { updateProposal, getKazooProposals } from '../../features/proposalSlice'
import {
	getVendorDetailsForReview,
	getUserReviewForProposal,
	createReview,
	updateReview,
	deleteReview,
	clearVendorDetails,
	clearCurrentReview,
} from '../../features/reviewSlice'
import { toastHelper } from '../../util/toastHelper'
import { formatCommonAmount } from '../../util/formatAmount'

export default function ConfirmedTable({ data }) {
	const navigate = useNavigate()
	const { kazooId } = useParams()

	const rows = useMemo(
		() =>
			data?.length > 0
				? data.filter(
					(d) =>
						d.status === 'Approved' &&
						d.paymentStatus?.toLowerCase() !== 'cancelled'
				)
				: [],
		[data]
	)
	const columns = [
		{
			field: 'vendorName',
			headerName: 'Vendor',
			flex: 1,
			minWidth: 100,
			valueGetter: (value, params) => {
				return value || params.businessName || params.email
			},
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			minWidth: 100,
			valueGetter: (value) => {
				return value || 'invited'
			},
		},
		{
			field: 'paymentDueDate',
			headerName: 'Due Date',
			flex: 1,
			minWidth: 100,
			valueGetter: (value) => {
				return formatCommonDate(value) || '--'
			},
		},
		{
			field: 'budget',
			headerName: 'Budget',
			flex: 1,
			minWidth: 100,
			renderCell: (params) =>
				params?.value ? formatCommonAmount(params?.value) : '--',
		},
		{
			field: 'bidAmount',
			headerName: 'Vendors Bid',
			flex: 1,
			minWidth: 100,
			renderCell: (params) => {
				const amount = params?.value || params.row.bidAmount
				const budget =
					params.row.status !== 'New' ? params.row.budget : ''
				return amount
					? formatCommonAmount(amount)
					: budget
						? formatCommonAmount(budget)
						: '--'
			},
		},
		{
			field: 'deposit',
			headerName: 'Deposit',
			flex: 1,
			minWidth: 100,
			renderCell: (params) =>
				params?.value ? formatCommonAmount(params?.value) : '--',
		},
		{
			field: 'paymentStatus',
			headerName: 'Status',
			flex: 1,
			minWidth: 200,
			renderCell: (params) => {
				const status = params.value
				if (!status) return '--'

				return (
					<StatusBtn
						label={status}
						variant={
							status?.toLowerCase() === 'paid'
								? 'completed'
								: status === 'pending'
									? 'pending'
									: 'default' // Default value in case none of the conditions match
						}
						size="small"
					/>
				)
			},
		},
		{
			field: 'action',
			headerName: 'Action',
			flex: 0,
			minWidth: 180,
			headerClassName: 'header-column-right',
			sortable: false,
			filterable: false,
			align: 'right',
			renderCell: (params) => {
				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
							justifyContent: 'flex-end',
							height: '100%',
						}}
					>
						{
							!params.row?.email &&
							<>
								{params.row.paymentStatus?.toLowerCase() === 'paid' && (
									<ButtonCustom
										variant="outlined"
										size="sm"
										onClick={() => handleRateNowClick(params.row)}
									>
										<img
											src="/images/star-bordered.svg"
											alt="star-bordered.svg"
										/>
										Rate Now
									</ButtonCustom>
								)}
								{params.row.paymentStatus?.toLowerCase() !== 'paid' && (
									<ButtonCustom
										variant="default"
										size="sm"
										onClick={(e) =>
											toastHelper(
												"Vendor has not set up payments processing through On Time Bride, please contact your vendor for payment details.",
												'info'
											)
										}
									>
										Pay
									</ButtonCustom>
								)}
							</>
						}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								height: '100%',
							}}
						>
							<ButtonCustom
								variant="outlined"
								size="sm"
								data-cy="kebab"
								onClick={(e) => openMenu(e, params.row)}
							>
								<img src="/images/more-horizontal.svg" alt="" />
							</ButtonCustom>
						</Box>
					</div>
				)
			},
		},
	]
	const [text, setText] = useState('')
	const [rating, setRating] = useState(0)
	const [selectedProposal, setSelectedProposal] = useState(null)

	const [menuAnchor, setMenuAnchor] = React.useState(null)
	const [menuRow, setMenuRow] = React.useState(null)
	const dispatch = useDispatch()
	const modal = useSelector((s) => s.common.modal)
	const { vendorDetails, currentReview, loading } = useSelector(
		(s) => s.review
	)

	// Load existing review when modal opens
	useEffect(() => {
		if (modal === 'rateModal' && selectedProposal) {
			// Fetch vendor details
			dispatch(getVendorDetailsForReview(selectedProposal.vendorId))
			// Check if user has already reviewed
			dispatch(getUserReviewForProposal(selectedProposal.id))
		}
	}, [modal, selectedProposal, dispatch])

	// Populate form when review is loaded
	useEffect(() => {
		if (currentReview) {
			setRating(currentReview.rating || 0)
			setText(currentReview.comment || '')
		} else {
			setRating(0)
			setText('')
		}
	}, [currentReview])

	const handleRateNowClick = (row) => {
		setSelectedProposal(row)
		dispatch(openModal('rateModal'))
	}

	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchor(e.currentTarget)
		setMenuRow(row)
	}

	const closeMenu = () => {
		setMenuAnchor(null)
		setMenuRow(null)
	}
	const handleClose = () => {
		dispatch(closeModal())
		setRating(0)
		setText('')
		setSelectedProposal(null)
		dispatch(clearVendorDetails())
		dispatch(clearCurrentReview())
	}

	const handleDeleteReview = async () => {
		if (!currentReview?._id) return

		const confirmDelete = window.confirm(
			'Are you sure you want to delete this review?'
		)
		if (!confirmDelete) return

		try {
			await dispatch(deleteReview(currentReview._id)).unwrap()
			handleClose()
		} catch (error) {
			console.error('Error deleting review:', error)
		}
	}

	const handleSubmitReview = async () => {
		if (!selectedProposal) return

		if (rating === 0) {
			alert('Please select a rating')
			return
		}

		if (text.trim().length < 10) {
			alert('Review must be at least 10 characters')
			return
		}

		const reviewData = {
			vendorId: selectedProposal.vendorId,
			proposalId: selectedProposal.id,
			rating,
			comment: text.trim(),
		}

		try {
			if (currentReview) {
				// Update existing review
				await dispatch(
					updateReview({
						reviewId: currentReview._id,
						rating,
						comment: text.trim(),
					})
				).unwrap()
			} else {
				// Create new review
				await dispatch(createReview(reviewData)).unwrap()
			}
			handleClose()
		} catch (error) {
			console.error('Error submitting review:', error)
		}
	}
	return (
		<>
			<div style={{ gridTemplateColumns: '1fr 1fr' }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',

						'@media (max-width: 900px)': {
							// height: 'calc(100vh - 390px)',
						},
					}}
				>
					<Box sx={{ flex: 1, minHeight: 0 }}>
						<TableData
							rows={rows}
							getRowId={(row) => row.id}
							columns={columns}
							height="75dvh"
							checkboxSelection={false}
						/>
					</Box>
					<KebabMenu
						anchorEl={menuAnchor}
						open={Boolean(menuAnchor)}
						onClose={closeMenu}
						items={[
							{
								key: 'view_profile',
								label: 'View Profile',
								icon: (
									<img
										src="/images/buliding.svg"
										alt="view_profile"
									/>
								),
								onClick: () =>
									navigate(`/profile/${menuRow?.vendorId}`),
							},

							{
								key: 'chat_now',
								label: 'Chat Now',
								icon: (
									<img
										src="/images/chat-icon.svg"
										alt="chat_now"
									/>
								),
								onClick: () =>
									navigate(
										kazooId
											? `/wedding/chat/${kazooId}`
											: '/chat',
										{
											state: {
												selectedUser: {
													_id: menuRow.vendorId,
													businessName:
														menuRow.vendorName,
													email: menuRow.email,
													profilePic:
														menuRow.profilePic ||
														menuRow.vendorProfilePic ||
														'',
												},
											},
										}
									),
							},
							{
								key: 'delete',
								label: 'Delete',
								icon: (
									<img
										src="/images/DeleteModal.svg"
										alt="delete"
									/>
								),
								onClick: () => {
									dispatch(openModal('removeProposal'))
									dispatch(setModalData(menuRow?.id))
									closeMenu()
								},
								color: 'error.main',
							},
						]}
					/>
				</Box>
			</div>
			<CommonModal
				open={modal === 'rateModal'}
				handleClose={handleClose}
				heading={currentReview ? 'Update Review' : 'Rate Now'}
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							size="lg"
							onClick={handleClose}
							disabled={loading}
						>
							Cancel
						</ButtonCustom>
						{currentReview && (
							<ButtonCustom
								variant="outlined"
								size="lg"
								onClick={handleDeleteReview}
								disabled={loading}
								sx={{
									color: 'error.main',
									borderColor: 'error.main',
								}}
							>
								{loading ? 'Deleting...' : 'Delete'}
							</ButtonCustom>
						)}
						<ButtonCustom
							size="lg"
							onClick={handleSubmitReview}
							disabled={loading}
						>
							{loading
								? 'Submitting...'
								: currentReview
									? 'Update Review'
									: 'Submit'}
						</ButtonCustom>
					</>
				}
			>
				<form className={styles.rateModalForm}>
					<Grid container spacing={{ xs: '12px', md: '24px' }}>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.vendorDetail}>
								<div className={styles.vendorImg}>
									<img
										alt="vendor-profile"
										src={
											vendorDetails?.businessProfilePic ||
											'/images/vendor-card-img.png'
										}
									/>
								</div>
								<div className={styles.vendorInfo}>
									<div className={styles.vendorName}>
										<Typography variant="h6">
											{vendorDetails?.businessType ||
												'Vendor'}
										</Typography>
										<Typography variant="h3">
											{vendorDetails?.businessName ||
												'Loading...'}
										</Typography>
									</div>
									{vendorDetails?.businessAddress && (
										<div className={styles.vendorLocation}>
											<img
												src="/images/location.svg"
												alt="location"
											/>
											<Typography variant="h3">
												{vendorDetails.businessAddress}
											</Typography>
										</div>
									)}
								</div>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<Box
								sx={{ '& > legend': { mt: 2 } }}
								className={styles.rating}
							>
								<Rating
									name="vendor-rating"
									value={rating}
									onChange={(event, newValue) => {
										setRating(newValue || 0)
									}}
									max={5}
									icon={
										<img
											src="/images/star.svg"
											alt="filled icon"
											style={{
												width: '23px',
												height: '23px',
											}}
										/>
									}
									emptyIcon={
										<img
											src="/images/star-without-fill.svg"
											alt="empty icon"
											style={{
												width: '23px',
												height: '23px',
											}}
										/>
									}
								/>
							</Box>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<label className={styles.label}>
								Write your review
							</label>
							<TextArea
								placeholder="Write here"
								value={text}
								onChange={(e) => setText(e.target.value)}
							/>
						</Grid>
					</Grid>
				</form>
			</CommonModal>
		</>
	)
}
